import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { format, startOfDay, isAfter } from "date-fns";


import CustomAutocomplete from "../../../components/CustomAutocomplete";
import {
  ControlpanelPrimaryButton,
  ControlpanelSecondaryButton,
  GrayIconButton,
} from "../../../components/StyledButton";
import { CustomDialog } from "../../../components/StyledComponents";
import CustomDateTimePicker from "../../../components/CustomDateTimeRangePicker";
import CustomInput from "../../../components/CustomInput";
import CustomSelect from "../../../components/CustomSelect";

import { useAuth } from "../../../context/AuthContext";
import { addRequestSwap } from "../../../axios/Shift/ShiftApi";
import { getRosterItems } from "../../../axios/RosterItem/RosterItemApi";
import { getListUserGroup } from "../../../axios/Group/GroupUrlApi";
import { useLoading } from "../../../context/LoadingContext";
import { fromZonedTime } from "date-fns-tz";

const isShiftExpired = (date, shiftTime, timeZone) => {
  if (!date || !shiftTime) return false;

  const [start, end] = shiftTime.split(" - ");

  const dateStr = format(date, "yyyy-MM-dd");

  const startDateTimeStr = `${dateStr} ${start}`;
  const startUtc = fromZonedTime(startDateTimeStr, timeZone);

  const now = new Date();

  return isAfter(now, startUtc);
};

const AddShiftRequestDialog = ({
  onClose,
  usersData,
  shiftsData,
  selectedDate,
  rosterItemId,
  selectedRosterItem,
  rosterId,
  usersDataGroup,
  selectedUser,
  usersGroup,
}) => {
  const { t } = useTranslation();
  const { isAdmin, isEmployee, profile, genaralSettingTimeZone } = useAuth();
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [userGroup, setUserGroup] = useState([]);
  const [fromUserGroup, setFromUserGroup] = useState([]);
  const [toUserGroup, setToUserGroup] = useState([]);
  const [show, hide] = useLoading();

  // Tour fake — không gọi API roster/group
  const isTourFake =
    rosterId === "tour-fake-roster" ||
    selectedRosterItem?.rosterItemId === "tour-fake-item" ||
    String(selectedRosterItem?.userId || "").startsWith("tour-fake");

  const fetchRosterItem = async (payload, setState) => {
    if (isTourFake) {
      // Seed từ item giả / shiftsData để dropdown shift vẫn có option
      const fakeItems =
        selectedRosterItem?.shiftId
          ? [
              {
                rosterItemId: selectedRosterItem.rosterItemId,
                userId: selectedRosterItem.userId,
                groupId: selectedRosterItem.groupId,
                shiftId: selectedRosterItem.shiftId,
                shiftName: selectedRosterItem.shiftName || "Morning",
                shiftTime: selectedRosterItem.shiftTime || "08:00 - 17:00",
                weekDate: selectedRosterItem.weekDate,
              },
            ]
          : (shiftsData || []).slice(0, 1).map((s) => ({
              rosterItemId: "tour-fake-item",
              shiftId: s.shiftId,
              shiftName: s.shiftName,
              shiftTime: s.shiftTime || `${s.shiftStart} - ${s.shiftEnd}`,
            }));
      setState(fakeItems);
      return;
    }

    try {
      const res = await getRosterItems(payload);

      let data = res?.data?.data || [];

      const { userId, groupIds } = payload;

      // case 1: chỉ group
      if (groupIds && !userId) {
        data = data.filter((item) => item.groupId === groupIds);
      }

      // case 2: chỉ user
      if (userId && !groupIds) {
        data = data.filter((item) => item.userId === userId);
      }

      // case 3: user trong group
      if (userId && groupIds) {
        const filteredByUser = data.filter((item) => item.userId === userId);

        data =
          filteredByUser.length > 0
            ? filteredByUser
            : data.filter((item) => item.groupId === groupIds);
      }

      setState(data);
    } catch (e) {
      setState([]);
    }
  };

  const initialFormData = {
    user: selectedRosterItem
      ? {
        userId: selectedRosterItem.userId,
        fullName: selectedRosterItem.fullName,
      }
      : profile,

    userId: selectedRosterItem?.userId || profile?.userId,

    fromGroup: null,
    fromGroupId: selectedRosterItem?.groupId || null,

    toGroup: null,
    toGroupId: null,

    toUser: null,
    toUserId: null,

    fromRosterItemId: selectedRosterItem?.rosterItemId || 0,
    toRosterItemId: 0,

    fromDate: selectedRosterItem?.weekDate
      ? new Date(selectedRosterItem.weekDate)
      : new Date(),

    toDate: selectedRosterItem?.weekDate
      ? new Date(selectedRosterItem.weekDate)
      : new Date(),

    fromShiftId: selectedRosterItem?.shiftId || null,
    toShiftId: null,

    requestReason: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [fromRosterItems, setFromRosterItems] = useState([]);
  const [toRosterItems, setToRosterItems] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      if (isTourFake) return;

      const payload = {
        group: formData.fromGroupId,
        pageNo: 1,
        recordPerPage: 10000,
        langId: localStorage.getItem("i18nextLngId"),
      };

      try {
        const res = await getListUserGroup(payload);
        setFromUserGroup(res.data.data);
      } catch (error) {
        if (error.response?.status !== 401) {
          toast.error(t(error?.response?.data.message) || t('common.network-timeout'));
        }
      }
    };

    if (formData.fromGroupId) fetchData();
  }, [formData.fromGroupId, isTourFake]);

  useEffect(() => {
    const fetchData = async () => {
      if (isTourFake) return;

      const payload = {
        group: formData.toGroupId,
        pageNo: 1,
        recordPerPage: 10000,
        langId: localStorage.getItem("i18nextLngId"),
      };

      try {
        const res = await getListUserGroup(payload);
        setToUserGroup(res.data.data);
      } catch (error) {
        if (error.response?.status !== 401) {
          toast.error(t(error?.response?.data.message) || t('common.network-timeout'));
        }
      }
    };

    if (formData.toGroupId) fetchData();
  }, [formData.toGroupId, isTourFake]);

  useEffect(() => {
    if (!formData.fromDate) return;
    if (isTourFake) {
      fetchRosterItem({}, setFromRosterItems);
      return;
    }

    const payload = {
      rosterId: rosterId,
      userId: formData.userId,
      fromDate: format(formData.fromDate, "yyyy-MM-dd"),
      endDate: format(formData.fromDate, "yyyy-MM-dd"),
      isEnabled: true,
      groupIds: formData.fromGroupId ?? null,
      isSwapShift: false,
    };

    fetchRosterItem(payload, setFromRosterItems);
  }, [formData.userId, formData.fromDate, rosterId, formData.fromGroupId, isTourFake]);

  useEffect(() => {
    if (!formData.toDate) return;

    if (!formData.toUserId && !formData.toGroupId) {
      setToRosterItems([]);
      return;
    }

    if (isTourFake) {
      fetchRosterItem({}, setToRosterItems);
      return;
    }

    const payload = {
      rosterId,
      userId: formData.toUserId ?? null,
      groupIds: formData.toGroupId ?? null,
      fromDate: format(formData.toDate, "yyyy-MM-dd"),
      endDate: format(formData.toDate, "yyyy-MM-dd"),
      isEnabled: true,
      isSwapShift: false,
    };

    fetchRosterItem(payload, setToRosterItems);
  }, [formData.toUserId, formData.toGroupId, formData.toDate, rosterId, isTourFake]);

  const fromShifts = fromRosterItems
    .filter(
      (item) =>
        !isShiftExpired(
          formData.fromDate,
          item.shiftTime,
          genaralSettingTimeZone
        )
    )
    .map((item) => ({
      shiftId: item.shiftId,
      shiftName: item.shiftName,
      shiftTime: item.shiftTime,
    }));

  const isNoFromShift = fromShifts.length === 0;

  const fromShiftOptions = isNoFromShift
    ? [{ shiftId: "", shiftName: t("common.no-shifts-available") }]
    : fromShifts;

  const isSameUser = formData.userId && formData.userId === formData.toUserId;

  const swapShifts = isSameUser
    ? (shiftsData || []).filter(
      (shift) =>
        !isShiftExpired(
          formData.toDate,
          shift.shiftTime,
          genaralSettingTimeZone
        )
    )
    : toRosterItems.filter(
      (item) =>
        !isShiftExpired(
          formData.toDate,
          item.shiftTime,
          genaralSettingTimeZone
        )
    );

  const isNoSwapShift = swapShifts.length === 0;

  const swapShiftOptions = isNoSwapShift
    ? [{ shiftId: "", shiftName: t("common.no-shifts-available") }]
    : swapShifts;

  const MAX_REASON_LENGTH = 255;

  const sortedShifts = [...(shiftsData || [])].sort(
    (a, b) => b.shiftId - a.shiftId,
  );

  const [isFormValid, setIsFormValid] = useState(false);
  const [swapRosterItems, setSwapRosterItems] = useState([]);

  useEffect(() => {
    if (swapRosterItems.length === 0) return;

    setFormData((prev) => ({
      ...prev,
      toRosterItemId: swapRosterItems[0].rosterItemId,
    }));
  }, [swapRosterItems]);

  const handleCloseDialog = () => {
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "toShiftId") {
      const rosterItem = toRosterItems.find((item) => item.shiftId === value);

      setFormData((prev) => ({
        ...prev,
        toShiftId: value,
        toRosterItemId: rosterItem?.rosterItemId ?? null,
      }));

      return;
    }

    if (name === "requestReason" && value?.length > MAX_REASON_LENGTH) return;

    if (name === "toGroup") {
      const groupId = Number(value) > 0 ? Number(value) : null;

      setSelectedGroup(groupId);

      setFormData((prev) => ({
        ...prev,
        toGroupId: groupId,
        toUser: null, // ✅ reset UI
        toUserId: null, // reset id
        toShiftId: null,
      }));

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (isTourFake) {
      handleCloseDialog();
      return;
    }

    try {
      const payload = {
        ...formData,
        fromDate: format(formData.fromDate, "yyyy-MM-dd"),
        toDate: format(formData.toDate, "yyyy-MM-dd"),
        langId: localStorage.getItem("i18nextLngId"),
      };

      await addRequestSwap(payload);

      toast.success(t("common.add-shift-request-successfully"));
      handleCloseDialog();
    } catch (error) {
      console.error(error.response.data.message);
      toast.error(error.response.data.message);
    }
  };

  useEffect(() => {
    const isValid =
      (formData.userId != null || formData.fromGroupId != null) &&
      (formData.toUserId != null || formData.toGroupId != null) &&
      formData.fromShiftId != null &&
      formData.toShiftId != null &&
      formData.requestReason.trim() !== "";

    setIsFormValid(isValid);
  }, [formData]);

  const filteredUsersForUser = usersData?.filter(
    (u) => u.userId !== formData.toUserId,
  );

  const filteredUsersForSwapUser = usersData?.filter(
    (u) => u.userId !== formData.userId,
  );

  const userGroupOptions = useMemo(() => {
    return (usersDataGroup || []).map((u) => ({
      userId: u.userId,
      fullName: u.fullName,
    }));
  }, [usersDataGroup]);

  useEffect(() => {
    const fetchData = async () => {
      if (isTourFake) return;

      const payload = {
        group: selectedGroup,
        pageNo: 1,
        recordPerPage: 10000,
        langId: localStorage.getItem("i18nextLngId"),
      };
      show();
      try {
        const response = await getListUserGroup(payload);
        setUserGroup(response.data.data);
      } catch (error) {
        if (error.response) {
          if (error.response.status !== 401) {
            toast.error(t(error?.response.data));
          }
        } else {
          toast.error(t('common.network-timeout'));
        }
      } finally {
        hide();
      }
    };
    if (selectedGroup) {
      fetchData();
    }
  }, [selectedGroup, t, isTourFake]);

  const groupOptionsWithNone = [
    { groupId: null, groupName: t("common.no-group") },
    ...usersGroup,
  ];

  useEffect(() => {
    if (fromRosterItems.length === 0) return;

    setFormData((prev) => {
      // nếu đã chọn rồi thì không override
      if (prev.fromShiftId) return prev;

      const firstItem = fromRosterItems[0];

      return {
        ...prev,
        fromShiftId: firstItem.shiftId,
        fromRosterItemId: firstItem.rosterItemId,
      };
    });
  }, [fromRosterItems]);

  useEffect(() => {
    const isSameUser = formData.userId && formData.userId === formData.toUserId;

    if (isSameUser) {
      if (!shiftsData?.length) return;

      const firstShift = shiftsData[0];

      setFormData((prev) => ({
        ...prev,
        toShiftId: firstShift.shiftId,
        toRosterItemId: null, // ⚠️ không có rosterItem
      }));

      return;
    }

    if (toRosterItems.length === 0) return;

    const firstItem = toRosterItems[0];

    setFormData((prev) => ({
      ...prev,
      toShiftId: firstItem.shiftId,
      toRosterItemId: firstItem.rosterItemId,
    }));
  }, [toRosterItems, formData.userId, formData.toUserId, shiftsData]);

  useEffect(() => {
    if (formData.fromGroupId && !formData.userId) {
      const userInGroup = fromUserGroup?.find(u => u.userId === profile.userId);

      if (userInGroup) {
        setFormData(prev => ({
          ...prev,
          userId: profile.userId,
          user: { userId: profile.userId, fullName: profile.fullName },
        }));
      } else {
        // Nếu profile không thuộc group thì không fill gì cả
        setFormData(prev => ({
          ...prev,
          userId: null,
          user: null,
        }));
      }
    }
  }, [formData.fromGroupId, fromUserGroup, profile]);
  return (
    <CustomDialog
      open
      transitionDuration={300}
      maxWidth={"xl"}
      onClose={handleCloseDialog}
    >
      <DialogTitle>
        <div className="flex justify-between">
          <div className="flex items-center gap-4">
            <p className="text-xl font-semibold">{t("common.add-request-shift")}</p>
          </div>

          <GrayIconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </GrayIconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        <div
          className="grid grid-cols-12 md:gap-x-10 gap-y-6"
          data-tour="shift-and-roster-add-request-input"
        >
          <div className="col-span-12 md:col-span-6 mt-4">
            <label>{t("common.your-group")}</label>

            <CustomSelect
              name="fromGroup"
              id="fromGroup"
              value={formData.fromGroupId}
              onChange={handleInputChange}
              options={groupOptionsWithNone}
              dataType="fromGroup"
              className="mt-2"
              readOnly={
                Boolean(selectedRosterItem?.userId) ||
                Boolean(selectedRosterItem?.groupId)
              }
            />
          </div>

          <div className="col-span-12 md:col-span-6 mt-4">
            <label>{t("common.swap-group")}</label>

            <CustomSelect
              name="toGroup"
              id="toGroup"
              value={formData.toGroupId}
              onChange={handleInputChange}
              options={groupOptionsWithNone}
              dataType="toGroup"
              className="mt-2"
            />
          </div>

          {/* USER */}
          <div className="col-span-12 md:col-span-6">
            <label>{t("common.staff")}</label>

            {formData.fromGroupId && isAdmin ? (
              <CustomAutocomplete
                className="min-w-60 mt-2"
                value={fromUserGroup?.find(u => u.userId === formData.userId) || null}
                options={fromUserGroup}
                dataType="user"
                freeSolo
                placeholder={t("common.content-161")}
                onDataChange={(name, value) => {
                  setFormData((prev) => ({
                    ...prev,
                    user: value,
                    userId: value?.userId || value?.id || null,
                    fromShiftId: null,
                  }));
                }}
              />
            ) : (
              <CustomInput
                className="min-w-60"
                value={
                  selectedRosterItem?.fullName ||
                  profile?.fullName || // user hiện tại
                  ""
                }
                readOnly
              />
            )}
          </div>

          {/* SWAP USER */}
          <div className="col-span-12 md:col-span-6">
            <label>{t("common.staff-swap")}</label>

            <CustomAutocomplete
              key={formData.toGroupId}
              className="min-w-60 mt-2"
              value={formData.toUser}
              options={
                formData.toGroupId == null ? userGroupOptions : toUserGroup
              }
              dataType="user"
              freeSolo
              placeholder={t("common.content-161")}
              onDataChange={(name, value) => {
                setFormData((prev) => ({
                  ...prev,
                  toUser: value,
                  toUserId: value?.userId || value?.id || null,
                  toShiftId: null,
                }));
              }}
            />
          </div>

          {/* CURRENT DATE */}
          <div className="col-span-12 md:col-span-6">
            <label>{t("common.date-work")}</label>
            <span className="text-red-500 ml-1">*</span>
            <div className="mt-2">
              <CustomDateTimePicker
                mode="single"
                value={formData.fromDate}
                onChange={(newValue) =>
                  setFormData((prev) => ({ ...prev, fromDate: newValue }))
                }
                disableTime
                showOnlyDate
                disabled
              />
            </div>
          </div>

          {/* SWAP DATE */}
          <div className="col-span-12 md:col-span-6">
            <label>{t("common.swap-date-work")}</label>
            <span className="text-red-500 ml-1">*</span>
            <div className="mt-2">
              <CustomDateTimePicker
                mode="single"
                value={formData.toDate}
                onChange={(newValue) =>
                  setFormData((prev) => ({
                    ...prev,
                    toDate: newValue,
                    toShiftId: null,
                  }))
                }
                minDate={startOfDay(new Date())}
                disableTime
                showOnlyDate
              />
            </div>
          </div>

          {/* FROM SHIFT */}
          <div className="col-span-12 md:col-span-6">
            <label>
              {t("common.current-shift")}
              <span className="text-red-500 ml-1">*</span>
            </label>

            <CustomSelect
              name="fromShiftId"
              value={formData.fromShiftId}
              onChange={handleInputChange}
              options={fromShiftOptions}
              className="mt-2"
              dataType="shift"
              readOnly={isNoFromShift}
            />
          </div>

          {/* TO SHIFT */}
          <div className="col-span-12 md:col-span-6">
            <label>
              {t("common.swap-current-shift")}
              <span className="text-red-500 ml-1">*</span>
            </label>

            <CustomSelect
              name="toShiftId"
              value={formData.toShiftId}
              onChange={handleInputChange}
              options={swapShiftOptions}
              className="mt-2"
              dataType="shift"
              readOnly={isNoSwapShift}
            />
          </div>

          {/* DESCRIPTION */}
          <div className="col-span-12">
            <label>
              {t("common.swap-reason")}
              <span className="text-red-500 ml-1">*</span>
            </label>

            <CustomInput
              name="requestReason"
              value={formData.requestReason}
              handleInputChange={handleInputChange}
              placeholder={t("common.description")}
            />

            {/* <div
              className={`flex justify-end text-xs mt-1 ${formData.requestReason?.length >= MAX_REASON_LENGTH
                ? "text-red-500"
                : "text-gray-500"
                }`}
            >
              {formData.requestReason?.length}/{MAX_REASON_LENGTH}
            </div> */}
          </div>
        </div>
      </DialogContent>

      <DialogActions>
        <div className="flex gap-2 px-4 py-4">
          <ControlpanelSecondaryButton onClick={handleCloseDialog}>
            <p>{t("button.close")}</p>
          </ControlpanelSecondaryButton>

          <ControlpanelPrimaryButton
            onClick={handleSubmit}
            disabled={!isFormValid}
          >
            <p>{t("button.confirm")}</p>
          </ControlpanelPrimaryButton>
        </div>
      </DialogActions>
    </CustomDialog>
  );
};

export default AddShiftRequestDialog;
